/**
 * Presentation Layer - TimeRangeSelector Component
 * 時間帯選択コンポーネント
 */

import React, { useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { AccessTime, Today as TodayIcon } from '@mui/icons-material';
import type { TimeRange } from '../../../../domain/medicalReservation/TimeRange';

/**
 * 日付オプション型
 */
interface DateOption {
  value: string;
  label: string;
}

/**
 * 時刻オプション型
 */
interface TimeOption {
  value: string;
  label: string;
}

/**
 * Props定義
 */
export interface TimeRangeSelectorProps {
  /** 現在の時間範囲 */
  timeRange: TimeRange;
  /** 時間範囲変更時のコールバック */
  onChange: (timeRange: TimeRange) => void;
  /** 無効化フラグ */
  isDisabled?: boolean;
  /** ローディング中フラグ */
  isLoading?: boolean;
  /** 翌日指定ボタン押下時のコールバック */
  onSetNextDay?: () => void;
  /** 選択した時間帯を適用する */
  onApply: () => void;
}

/**
 * 時間帯選択コンポーネント
 */
export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  timeRange,
  onChange,
  isDisabled = false,
  isLoading = false,
  onSetNextDay,
  onApply,
}) => {
  // 日付オプションの生成（今日と明日のみ）
  const dateOptions = useMemo((): DateOption[] => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    };

    const formatLabel = (date: Date): string => {
      const formatted = formatDate(date);
      const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
      const dayName = dayNames[date.getDay()];
      const isToday = date.toDateString() === today.toDateString();
      const isTomorrow = date.toDateString() === tomorrow.toDateString();

      if (isToday) {
        return `${formatted} (今日・${dayName})`;
      } else if (isTomorrow) {
        return `${formatted} (明日・${dayName})`;
      }
      return `${formatted} (${dayName})`;
    };

    return [
      {
        value: formatDate(today),
        label: formatLabel(today)
      },
      {
        value: formatDate(tomorrow),
        label: formatLabel(tomorrow)
      }
    ];
  }, []);

  // 時間オプションの生成（15分刻み）
  const allTimeOptions = useMemo((): TimeOption[] => {
    const options: TimeOption[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = String(hour).padStart(2, '0');
        const minuteStr = String(minute).padStart(2, '0');
        const value = `${hourStr}:${minuteStr}`;
        options.push({
          value,
          label: value
        });
      }
    }
    return options;
  }, []);

  // 日付をYYYY/MM/DD形式に変換
  const formatDateForDisplay = (dateStr: string): string => {
    return dateStr.replace(/-/g, '/');
  };

  // 日付をYYYY-MM-DD形式に変換（内部保存用）
  const formatDateForStorage = (dateStr: string): string => {
    return dateStr.replace(/\//g, '-');
  };

  // 終了時間の選択肢を開始時間に基づいて制限
  const endTimeOptions = useMemo((): TimeOption[] => {
    const startDateDisplay = formatDateForDisplay(timeRange.startDate);
    const endDateDisplay = formatDateForDisplay(timeRange.endDate);

    if (!timeRange.startDate || !timeRange.startTime) {
      return allTimeOptions;
    }

    // 同じ日付の場合は開始時間より後の時間のみ表示
    if (startDateDisplay === endDateDisplay) {
      return allTimeOptions.filter(option => option.value > timeRange.startTime);
    }

    // 異なる日付の場合は全時間表示
    return allTimeOptions;
  }, [timeRange.startDate, timeRange.startTime, timeRange.endDate, allTimeOptions]);

  // 終了日の選択肢を開始日に基づいて制限
  const endDateOptions = useMemo((): DateOption[] => {
    if (!timeRange.startDate) {
      return dateOptions;
    }

    const startDateDisplay = formatDateForDisplay(timeRange.startDate);
    // 開始日以降の日付のみ表示
    return dateOptions.filter(option => option.value >= startDateDisplay);
  }, [timeRange.startDate, dateOptions]);

  // 開始日が変更された時の処理
  const handleStartDateChange = useCallback((startDate: string) => {
    const startDateStorage = formatDateForStorage(startDate);
    let newEndDate = timeRange.endDate;
    let newEndTime = timeRange.endTime;

    // 終了日が開始日より前の場合は開始日に合わせる
    if (newEndDate && formatDateForDisplay(newEndDate) < startDate) {
      newEndDate = startDateStorage;
      newEndTime = ''; // 終了時間もリセット
    }

    onChange({
      ...timeRange,
      startDate: startDateStorage,
      endDate: newEndDate,
      endTime: newEndTime
    });
  }, [timeRange, onChange]);

  // 開始時間が変更された時の処理
  const handleStartTimeChange = useCallback((startTime: string) => {
    let newEndTime = timeRange.endTime;

    // 同じ日付で終了時間が開始時間より前の場合はリセット
    if (formatDateForDisplay(timeRange.startDate) === formatDateForDisplay(timeRange.endDate) &&
        newEndTime && newEndTime <= startTime) {
      newEndTime = '';
    }

    onChange({
      ...timeRange,
      startTime,
      endTime: newEndTime
    });
  }, [timeRange, onChange]);

  // 終了日が変更された時の処理
  const handleEndDateChange = useCallback((endDate: string) => {
    const endDateStorage = formatDateForStorage(endDate);
    onChange({
      ...timeRange,
      endDate: endDateStorage,
      endTime: '' // 日付が変わったら時間をリセット
    });
  }, [timeRange, onChange]);

  // 終了時間が変更された時の処理
  const handleEndTimeChange = useCallback((endTime: string) => {
    onChange({
      ...timeRange,
      endTime
    });
  }, [timeRange, onChange]);

  // 選択状態の表示
  const getSelectedRangeText = (): string => {
    if (!timeRange.startDate || !timeRange.startTime || !timeRange.endDate || !timeRange.endTime) {
      return '時間帯未指定（全データを表示）';
    }
    const startDateDisplay = formatDateForDisplay(timeRange.startDate);
    const endDateDisplay = formatDateForDisplay(timeRange.endDate);
    return `${startDateDisplay} ${timeRange.startTime} 〜 ${endDateDisplay} ${timeRange.endTime}`;
  };

  const isComplete = timeRange.startDate && timeRange.startTime && timeRange.endDate && timeRange.endTime;

  // 現在の日付値をYYYY/MM/DD形式で取得
  const startDateDisplay = formatDateForDisplay(timeRange.startDate);
  const endDateDisplay = formatDateForDisplay(timeRange.endDate);

  return (
    <Card
      sx={{
        borderRadius: 2,
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <AccessTime sx={{ fontSize: 20, color: '#9f545c' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 17 }}>
              診療時間帯の指定
            </Typography>
          </Box>
        }
        subheader="CSVデータを抽出する開始・終了日時を選択してください"
        sx={{ width: '100%', maxWidth: 808, px: { xs: 2, sm: 3 }, pt: 2.5, pb: 1.5 }}
      />
      <CardContent sx={{ width: '100%', maxWidth: 808, px: { xs: 2, sm: 3 }, pt: 0, pb: '24px !important' }}>
        <Alert severity={isComplete ? 'success' : 'info'} sx={{ mb: 2.5, py: 0, maxWidth: 540, '& .MuiAlert-message': { fontWeight: 600 } }}>
          {getSelectedRangeText()}
        </Alert>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'max-content 24px max-content' }, gap: { xs: 2, md: 1.5 }, alignItems: 'end' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>開始日時</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '204px 112px' }, gap: 1.5 }}>
              <FormControl variant="outlined" size="small" fullWidth disabled={isDisabled || isLoading}>
                <InputLabel>日付</InputLabel>
                <Select value={startDateDisplay} onChange={(e) => handleStartDateChange(e.target.value)} label="日付">
                  <MenuItem value=""><em>選択してください</em></MenuItem>
                  {dateOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" fullWidth disabled={isDisabled || isLoading || !timeRange.startDate}>
                <InputLabel>時刻</InputLabel>
                <Select value={timeRange.startTime} onChange={(e) => handleStartTimeChange(e.target.value)} label="時刻">
                  <MenuItem value=""><em>選択してください</em></MenuItem>
                  {allTimeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Typography aria-hidden="true" sx={{ textAlign: 'center', color: 'text.secondary', pb: { xs: 0, md: 1 }, lineHeight: 1 }}>〜</Typography>

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>終了日時</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: '204px 112px' }, gap: 1.5 }}>
              <FormControl variant="outlined" size="small" fullWidth disabled={isDisabled || isLoading}>
                <InputLabel>日付</InputLabel>
                <Select value={endDateDisplay} onChange={(e) => handleEndDateChange(e.target.value)} label="日付">
                  <MenuItem value=""><em>選択してください</em></MenuItem>
                  {endDateOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" fullWidth disabled={isDisabled || isLoading || !timeRange.endDate}>
                <InputLabel>時刻</InputLabel>
                <Select value={timeRange.endTime} onChange={(e) => handleEndTimeChange(e.target.value)} label="時刻">
                  <MenuItem value=""><em>選択してください</em></MenuItem>
                  {endTimeOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

      </CardContent>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
          {onSetNextDay && (
            <Button variant="outlined" onClick={onSetNextDay} disabled={isDisabled || isLoading} startIcon={<TodayIcon />}>
              翌日を指定
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onApply}
            disabled={isDisabled || isLoading || !isComplete}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ ml: 'auto' }}
          >
            {isLoading ? '処理中...' : '時間帯を設定'}
          </Button>
      </Box>
    </Card>
  );
};
